import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { Skeleton } from 'components/ui/skeleton';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from 'components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from 'components/ui/table';
import { FileText, Search, SquarePen } from 'lucide-react';
import { MultiSelectCombobox } from 'components/ui/multi-select-combobox';
import {
  listContracts, getJob, getContract, isTerminalJob, ouOptions, ContractListItem, ContractDetail, Option,
  BRANCH_LEVEL, currentFY,
} from './api';
import UploadContractModal from './UploadContractModal';
import { useBreadcrumbs } from 'contexts/BreadcrumbsContext';
import { useAuth } from 'contexts/AuthContext';

// same pill style as the badges on the Users page (ring-inset)
const STATUS_STYLES: Record<string, string> = {
  active: 'bg-green-50 text-green-700 ring-green-600/20',
  processing: 'bg-blue-50 text-blue-700 ring-blue-700/10',
  needs_completion: 'bg-amber-50 text-amber-800 ring-amber-600/20',
  draft: 'bg-gray-50 text-gray-600 ring-gray-500/10',
  superseded: 'bg-gray-50 text-gray-500 ring-gray-500/10',
  processing_failed: 'bg-red-50 text-red-700 ring-red-600/10',
  expired: 'bg-red-50 text-red-700 ring-red-600/10',
};
const STATUS_LABELS: Record<string, string> = { processing_failed: 'Processing failed', needs_completion: 'Needs completion' };

const STATUS_FILTERS = [
  { value: 'all', label: 'All statuses' },
  { value: 'needs_completion', label: 'Needs completion' },
  { value: 'processing', label: 'Processing' },
  { value: 'processing_failed', label: 'Processing failed' },
  { value: 'active', label: 'Active' },
  { value: 'expired', label: 'Expired' },
];
const PAGE_SIZES = [10, 20, 50];
const EXPIRY_WARNING_DAYS = 30;
const COLUMNS = 8;   // incl. the actions column

type ListState = {
  search: string; status: string; booking: string[]; billing: string[];
  limit: number; offset: number; rows: ContractListItem[]; total: number;
};
// Last list shown, kept across navigation: coming back from a contract renders it
// immediately (same search / filter / page) while a fresh copy loads in the background.
let lastList: ListState | null = null;

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] || STATUS_STYLES.draft;
  const label = STATUS_LABELS[status] || status.charAt(0).toUpperCase() + status.slice(1).replace(/_/g, ' ');
  return (
    <span className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-1 text-xs font-medium ring-1 ring-inset ${cls}`}>
      {label}
    </span>
  );
}

const fmtDate = (d: string | null) => (d ? dayjs(d).format('DD MMM YYYY') : '—');

function Validity({ c }: { c: ContractListItem }) {
  const daysLeft = c.end_date ? dayjs(c.end_date).diff(dayjs().startOf('day'), 'day') : null;
  const expiringSoon = c.status === 'active' && daysLeft !== null && daysLeft >= 0 && daysLeft <= EXPIRY_WARNING_DAYS;
  return (
    <div className="whitespace-nowrap">
      <div>{fmtDate(c.start_date)} – {fmtDate(c.end_date)}</div>
      {expiringSoon && (
        <div className="text-xs font-medium text-amber-700">
          {daysLeft === 0 ? 'Expires today' : `Expires in ${daysLeft} day${daysLeft === 1 ? '' : 's'}`}
        </div>
      )}
    </div>
  );
}

const ContractsPage: React.FC = () => {
  const navigate = useNavigate();
  // actions follow the role's contract permissions; the API enforces them too
  const perms = useAuth().userInfo?.perms ?? [];
  const canEdit = perms.includes('contracts.edit');       // "Edit Contract Details"
  const canUpload = perms.includes('contracts.upload');   // "Upload Contracts"
  const [editing, setEditing] = useState<ContractDetail | null>(null);
  const [editError, setEditError] = useState<string | null>(null);
  const openEdit = async (id: number) => {
    setEditError(null);
    try {
      setEditing(await getContract(id));   // the form needs the full details, not the list row
    } catch (e: any) {
      setEditError(e?.message || 'Could not open the contract for editing');
    }
  };
  const { setBreadcrumbs } = useBreadcrumbs();
  useEffect(() => {
    setBreadcrumbs([{ label: 'Contracts', href: '/contracts' }, { label: 'Customer Contracts' }]);
  }, [setBreadcrumbs]);

  const [rows, setRows] = useState<ContractListItem[]>(lastList?.rows ?? []);
  const [total, setTotal] = useState(lastList?.total ?? 0);
  const [search, setSearch] = useState(lastList?.search ?? '');
  const [status, setStatus] = useState(lastList?.status ?? 'all');
  const [booking, setBooking] = useState<string[]>(lastList?.booking ?? []);
  const [billing, setBilling] = useState<string[]>(lastList?.billing ?? []);
  const [branchOpts, setBranchOpts] = useState<Option[]>([]);
  useEffect(() => {
    ouOptions(BRANCH_LEVEL, currentFY()).then((r) => setBranchOpts(r.options)).catch(() => {});
  }, []);
  const [limit, setLimit] = useState(lastList?.limit ?? 20);
  const [offset, setOffset] = useState(lastList?.offset ?? 0);
  // true until the first response: "No contracts" must never show before we know
  const [loaded, setLoaded] = useState(lastList !== null);
  const [uploadOpen, setUploadOpen] = useState(false);

  const load = useCallback(async () => {
    const data = await listContracts({
      search, status: status === 'all' ? undefined : status, bookingBranches: booking, billingBranches: billing, limit, offset,
    });
    setRows(data.contracts);
    setTotal(data.total);
    setLoaded(true);
    lastList = { search, status, booking, billing, limit, offset, rows: data.contracts, total: data.total };
  }, [search, status, booking, billing, limit, offset]);

  // debounce typing in the search box; filter / page changes and the first load go immediately
  const firstLoad = useRef(true);
  const typing = useRef(false);
  useEffect(() => {
    const delay = firstLoad.current || !typing.current ? 0 : 250;
    firstLoad.current = false;
    typing.current = false;
    const t = setTimeout(load, delay);
    return () => clearTimeout(t);
  }, [load]);

  // a new search / filter starts again from the first page
  const onSearch = (v: string) => { typing.current = true; setSearch(v); setOffset(0); };
  const onStatus = (v: string) => { setStatus(v); setOffset(0); };
  const onBooking = (v: string[]) => { setBooking(v); setOffset(0); };
  const onBilling = (v: string[]) => { setBilling(v); setOffset(0); };

  // Poll the lightweight job-status endpoint (not the full contract) for each
  // in-flight row every 2s. When a job reaches a terminal state the worker has
  // already set the contract to needs_completion, so we just unlock the row.
  const pollTargets = rows
    .filter((c) => c.status === 'processing' && c.job_id)
    .map((c) => ({ id: c.id, jobId: c.job_id as string }));
  const pollKey = pollTargets.map((t) => `${t.id}:${t.jobId}`).join(',');
  useEffect(() => {
    if (!pollKey) return;
    const targets = pollKey.split(',').map((s) => {
      const [id, jobId] = s.split(':');
      return { id: Number(id), jobId };
    });
    const poll = async () => {
      const results = await Promise.allSettled(targets.map((t) => getJob(t.jobId)));
      const finished = new Map<number, string>();
      results.forEach((res, i) => {
        if (res.status === 'fulfilled' && isTerminalJob(res.value.status)) {
          finished.set(targets[i].id, res.value.status);
        }
      });
      if (finished.size) {
        setRows((prev) =>
          prev.map((row) => (finished.has(row.id)
            ? { ...row, status: 'needs_completion', job_status: finished.get(row.id)! }
            : row)),
        );
      }
    };
    const t = setInterval(poll, 2000);
    return () => clearInterval(t);
  }, [pollKey]);

  const filtered = !!search || status !== 'all' || booking.length > 0 || billing.length > 0;
  const branchItems = branchOpts.map((o) => ({ value: o.code, label: o.name }));
  const page = Math.floor(offset / limit) + 1;
  const pageCount = Math.max(1, Math.ceil(total / limit));

  return (
    <div className="flex flex-col gap-4 px-4 sm:px-6 pb-6">
      {/* toolbar — labelled filters on the left, count + primary action on the right (as on the other MIS pages) */}
      <div className="flex flex-wrap items-end gap-4">
        <div className="min-w-[220px] max-w-sm flex-1">
          <label className="mb-1 block text-sm font-medium text-gray-700">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Contract, customer, material…"
              title="Searches contract #, customer, customer division, location and material type"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
        <div className="w-44">
          <label className="mb-1 block text-sm font-medium text-gray-700">Status</label>
          <Select value={status} onValueChange={onStatus}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {STATUS_FILTERS.map((f) => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="w-48">
          <label className="mb-1 block text-sm font-medium text-gray-700">Booking Branch</label>
          <MultiSelectCombobox options={branchItems} value={booking} onValueChange={onBooking}
            placeholder="All branches" searchPlaceholder="Search branch…" />
        </div>
        <div className="w-48">
          <label className="mb-1 block text-sm font-medium text-gray-700">Billing Branch</label>
          <MultiSelectCombobox options={branchItems} value={billing} onValueChange={onBilling}
            placeholder="All branches" searchPlaceholder="Search branch…" />
        </div>
        {canUpload && <Button className="ml-auto h-10" onClick={() => setUploadOpen(true)}>Upload contract</Button>}
      </div>

      {editError && (
        <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
          <p className="text-sm text-destructive">{editError}</p>
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border bg-card">
        <Table className="[&_td]:px-3 [&_td]:py-1.5 [&_th]:h-auto [&_th]:px-3 [&_th]:py-2">
          <TableHeader className="bg-muted/50 [&_th]:whitespace-nowrap [&_th]:font-semibold [&_th]:text-foreground">
            <TableRow className="hover:bg-transparent">
              <TableHead>Contract #</TableHead>
              <TableHead>Customer</TableHead>
              <TableHead>Division</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Material Type</TableHead>
              <TableHead>Validity</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="w-px" aria-label="Actions" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {!loaded && Array.from({ length: 5 }).map((_, i) => (
              <TableRow key={`sk${i}`} className="hover:bg-transparent">
                {Array.from({ length: COLUMNS }).map((__, j) => (
                  <TableCell key={j}><Skeleton className="h-4 w-full max-w-[140px]" /></TableCell>
                ))}
              </TableRow>
            ))}
            {loaded && rows.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={COLUMNS} className="h-32 text-center">
                  <div className="flex flex-col items-center gap-1 text-muted-foreground">
                    <FileText className="h-6 w-6 opacity-50" />
                    <p className="text-sm">{filtered ? 'No contracts match these filters' : 'No contracts yet'}</p>
                    <p className="text-xs">{filtered ? 'Try adjusting your search or filters' : canUpload ? 'Upload a contract to get started' : 'Contracts shared with you will appear here'}</p>
                  </div>
                </TableCell>
              </TableRow>
            )}
            {rows.map((c) => {
              const processing = c.status === 'processing';
              const status = c.status === 'needs_completion' && c.job_status === 'failed' ? 'processing_failed' : c.status;
              return (
                <TableRow
                  key={c.id}
                  className={`font-normal ${processing ? 'cursor-default text-muted-foreground' : 'cursor-pointer hover:bg-muted/40'}`}
                  onClick={processing ? undefined : () => navigate(`/contracts/${c.id}`)}
                >
                  <TableCell className="whitespace-nowrap font-medium">{c.contract_number || `#${c.id}`}</TableCell>
                  <TableCell>{c.customer_name || c.customer_code}</TableCell>
                  <TableCell>{c.customer_division || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell>{c.customer_location || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell>{c.material_type || <span className="text-muted-foreground">—</span>}</TableCell>
                  <TableCell><Validity c={c} /></TableCell>
                  <TableCell>
                    <span className="inline-flex items-center gap-1.5">
                      {processing && <span className="h-3 w-3 animate-spin rounded-full border-b-2 border-blue-600" />}
                      <StatusBadge status={status} />
                    </span>
                  </TableCell>
                  <TableCell>
                    {canEdit && !processing && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
                        title="Edit contract details"
                        aria-label="Edit contract details"
                        onClick={(e) => { e.stopPropagation(); openEdit(c.id); }}
                      >
                        <SquarePen className="h-4 w-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* pagination — same layout as the Users page */}
      {loaded && total > 0 && (
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-6 text-sm text-muted-foreground">
            <div>Showing {offset + 1} to {Math.min(offset + limit, total)} of {total} contracts</div>
            <div className="flex items-center gap-2">
              <span>Rows per page:</span>
              <select
                className="h-8 w-16 rounded border border-input bg-background px-2 text-sm"
                value={limit}
                onChange={(e) => { setLimit(Number(e.target.value)); setOffset(0); }}
              >
                {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setOffset(Math.max(0, offset - limit))} disabled={offset === 0}>
              Previous
            </Button>
            <div className="flex items-center gap-1 text-sm">
              <span>Page</span><span className="font-medium">{page}</span><span>of</span><span className="font-medium">{pageCount}</span>
            </div>
            <Button variant="outline" size="sm" onClick={() => setOffset(offset + limit)} disabled={offset + limit >= total}>
              Next
            </Button>
          </div>
        </div>
      )}

      {canUpload && <UploadContractModal
        open={uploadOpen}
        onOpenChange={setUploadOpen}
        onCreated={() => { setUploadOpen(false); load(); }}
      />}
      {editing && (
        <UploadContractModal
          open
          onOpenChange={(v) => { if (!v) setEditing(null); }}
          editing={editing}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
};

export default ContractsPage;
